// Phase 5 (web revival) — TanStack Query client + cache key helpers.
//
// One central QueryClient so the WS listener (wsClient.ts) can invalidate
// arbitrary slices when server events fire. Keys are tuples
// (`['asset', id]` not `'asset/' + id`) so partial invalidation by prefix
// works the same way TanStack docs assume.

import { QueryClient } from '@tanstack/react-query';
import { ApiError } from '../api/client';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Stale-but-shown is the right UX for a photo library — keep
      // showing thumbnails while the background refetch resolves so
      // scrolling never sees an empty grid flash.
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      // Default retry: 1 attempt on network errors, none on 4xx/5xx
      // (the API client already turned those into thrown ApiErrors).
      retry: (failureCount, error) => {
        if (error instanceof ApiError) return false;
        return failureCount < 1;
      },
    },
    mutations: {
      retry: false,
    },
  },
});

// ───────────────────────────────────────────────────────────────────────
// Cache key namespacing — every read+write must go through these so the
// WS invalidation handlers below can find them.
// ───────────────────────────────────────────────────────────────────────

export const qk = {
  me: () => ['me'] as const,
  assets: (filter?: AssetsFilter) =>
    filter ? (['assets', filter] as const) : (['assets'] as const),
  asset: (id: string) => ['asset', id] as const,
  assetComments: (id: string) => ['asset', id, 'comments'] as const,
  assetReactions: (id: string) => ['asset', id, 'reactions'] as const,
  assetShares: (id: string) => ['asset', id, 'shares'] as const,
  assetEdits: (id: string) => ['asset', id, 'edits'] as const,
  feedSharedWithMe: () => ['feed', 'shared-with-me'] as const,
  albums: () => ['albums'] as const,
  album: (id: string) => ['album', id] as const,
  albumAssets: (id: string) => ['album', id, 'assets'] as const,
  users: () => ['users'] as const,
};

export type AssetsFilter = {
  include?: 'normal' | 'trashed';
  kind?: 'photo' | 'video' | 'live_photo' | 'burst';
  favorite?: boolean;
  archived?: boolean;
  ownerID?: string;
};
